import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { LayoutDashboard, Users, CalendarDays, BookOpen, Settings, Clock, LogOut, Menu, Video, DollarSign, MessageCircle, CalendarOff, PanelLeftClose, PanelLeft, Image, Baby, HardDrive, Play, BarChart3, AlertTriangle, Rocket, Boxes, Bell, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { getInitials } from '../lib/utils';
import { useState, useEffect } from 'react';
import api from '../lib/api';

const navItems = [
  // V14.5 Part 8 — `superadminOnly` items are hidden from non-super admins.
  // The user requested admins see only Batches, Calendar, and Users.
  { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', superadminOnly: true },
  { to: '/admin/batches', icon: BookOpen, label: 'Batches' },
  { to: '/admin/calendar', icon: CalendarDays, label: 'Calendar' },
  { to: '/admin/users', icon: Users, label: 'Users' },
  { to: '/admin/leaves', icon: CalendarOff, label: 'Leaves', superadminOnly: true },
  { to: '/admin/zoom', icon: Video, label: 'Zoom', superadminOnly: true },
  { to: '/admin/finance', icon: DollarSign, label: 'Finance', superadminOnly: true },
  { to: '/admin/soulai', icon: MessageCircle, label: 'SoulAI', superadminOnly: true },
  { to: '/admin/prenatal-coach', icon: Sparkles, label: 'Prenatal Coach', superadminOnly: true },
  { to: '/admin/banners', icon: Image, label: 'Banners', superadminOnly: true },
  { to: '/admin/daily-tips', icon: Baby, label: 'Daily Tips', superadminOnly: true },
  { to: '/admin/courses', icon: Play, label: 'Courses', superadminOnly: true },
  { to: '/admin/bundles', icon: Boxes, label: 'Bundles', indent: true, superadminOnly: true },
  { to: '/admin/storage', icon: HardDrive, label: 'Storage', superadminOnly: true },
  { to: '/admin/audit', icon: BarChart3, label: 'Audit', superadminOnly: true },
  // Sub-section under Audit — instructor-unavailable queue (was "Needs Reassignment")
  { to: '/admin/reassignments', icon: AlertTriangle, label: 'Instructor Unavailable', badgeKey: 'reassignments', indent: true, superadminOnly: true },
  { to: '/admin/settings', icon: Settings, label: 'Settings', superadminOnly: true },
  { to: '/admin/test-push', icon: Bell, label: 'Test Push', superadminOnly: true },
  { to: '/admin/launch-readiness', icon: Rocket, label: 'Launch Readiness', superadminOnly: true },
];

function SidebarContent({ onClose, collapsed }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [counts, setCounts] = useState({ reassignments: 0 });

  // V14.5 Part 8 — Filter sidebar by role. Non-super admins (`role=admin`)
  // see ONLY Batches, Calendar, Users. Superadmin sees everything.
  const isSuperadmin = user?.role === 'superadmin';
  const visibleNavItems = navItems.filter(item => isSuperadmin || !item.superadminOnly);

  // V14.1 — Pull pending-reassignment count once on mount + on route change.
  // Was: 60s polling — caused steady API load + render churn. Now event-driven.
  useEffect(() => {
    if (!isSuperadmin) return;  // only superadmin sees this badge anyway
    api.get('/api/admin/sessions/needs-reassignment')
      .then((r) => setCounts((c) => ({ ...c, reassignments: r.data.total || 0 })))
      .catch(() => {});
  }, [location.pathname, isSuperadmin]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 shrink-0 rounded-xl overflow-hidden bg-white flex items-center justify-center">
            <img src="/logo.png" alt="Soul Sanskar" className="h-14 w-14 object-contain scale-150" />
          </div>
          {!collapsed && (
            <div>
              <h1 className="font-heading text-base font-semibold tracking-tight text-foreground leading-tight">Soul Sanskar</h1>
              <p className="text-[10px] text-muted-foreground">LMS Panel</p>
            </div>
          )}
        </div>
      </div>
      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto scrollbar-thin">
        {visibleNavItems.map(item => {
          const badgeCount = item.badgeKey ? counts[item.badgeKey] : 0;
          return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 ${item.indent && !collapsed ? 'pl-7' : 'px-3'} pr-3 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${
                item.indent && !collapsed ? 'text-[13px]' : ''
              } ${
                isActive
                  ? 'bg-primary/10 text-primary border-l-2 border-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`
            }
            title={collapsed ? item.label : undefined}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && (
              <span className="flex-1 flex items-center justify-between">
                {item.label}
                {badgeCount > 0 && (
                  <Badge variant="destructive" className="h-5 min-w-[20px] px-1.5 text-[10px]" data-testid={`nav-badge-${item.badgeKey}`}>
                    {badgeCount}
                  </Badge>
                )}
              </span>
            )}
          </NavLink>
          );
        })}
      </nav>
      <div className={`p-3 border-t ${collapsed ? 'px-2' : ''}`}>
        {!collapsed && (
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary/10 text-primary text-xs">{getInitials(user?.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
        )}
        <Button variant="ghost" size="sm" className={`w-full ${collapsed ? 'px-2' : 'justify-start'} text-muted-foreground`} onClick={() => { logout(); navigate('/login'); }}>
          <LogOut className="h-4 w-4 mr-2" />{!collapsed && 'Sign out'}
        </Button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // V14.5 Part 8 — Route-level guard. Non-super admins are restricted to
  // Batches, Calendar, Users (and their sub-routes). If a non-super admin
  // manually types `/admin/finance` or `/admin/storage` etc. into the URL,
  // redirect them to `/admin/batches`. The sidebar filtering already
  // hides these links, but this guard prevents URL bar bypass.
  const isSuperadmin = user?.role === 'superadmin';
  const ALLOWED_ADMIN_PREFIXES = ['/admin/batches', '/admin/calendar', '/admin/users', '/admin/profile'];
  useEffect(() => {
    if (!user || isSuperadmin) return;
    const allowed = ALLOWED_ADMIN_PREFIXES.some(p => location.pathname.startsWith(p));
    if (!allowed) {
      navigate('/admin/batches', { replace: true });
    }
  }, [user, isSuperadmin, location.pathname, navigate]);

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex ${collapsed ? 'w-[68px]' : 'w-[248px]'} shrink-0 border-r bg-white/70 dark:bg-slate-900/70 backdrop-blur flex-col transition-all duration-300 relative`}>
        <SidebarContent onClose={() => {}} collapsed={collapsed} />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 z-10 h-6 w-6 rounded-full border bg-white dark:bg-slate-900 shadow-sm flex items-center justify-center hover:bg-muted transition-colors"
          data-testid="sidebar-collapse-button"
        >
          {collapsed ? <PanelLeft className="h-3 w-3" /> : <PanelLeftClose className="h-3 w-3" />}
        </button>
      </aside>
      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[248px]">
          <SidebarContent onClose={() => setMobileOpen(false)} collapsed={false} />
        </SheetContent>
      </Sheet>
      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b bg-white/60 dark:bg-slate-900/60 backdrop-blur flex items-center px-4 lg:px-6 gap-3">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
        </header>
        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
